import { useState, useEffect, useRef, useCallback } from 'react';
import type { WeddingEvent, EventStatus } from '@/lib/types';

export interface LiveState {
  nowEvent: WeddingEvent | null;
  nextEvent: WeddingEvent | null;
  progressPercent: number;
  minutesUntilNext: number;
  countdownTarget: Date | null;
  isRunningOver: boolean;
  allComplete: boolean;
}

export function useLiveState(
  events: WeddingEvent[],
  onAutoTransition: (eventId: string, status: EventStatus) => void
): LiveState {
  const [state, setState] = useState<LiveState>({
    nowEvent: null,
    nextEvent: null,
    progressPercent: 0,
    minutesUntilNext: 0,
    countdownTarget: null,
    isRunningOver: false,
    allComplete: false,
  });

  // Use refs to avoid stale closure issues
  const eventsRef = useRef(events);
  const callbackRef = useRef(onAutoTransition);
  const firedTransitions = useRef<Set<string>>(new Set());

  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  useEffect(() => {
    callbackRef.current = onAutoTransition;
  }, [onAutoTransition]);

  // Reset fired transitions when events list changes (e.g. new events added)
  useEffect(() => {
    firedTransitions.current = new Set();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events.map(e => e.id).join(',')]);

  const compute = useCallback(() => {
    const now = Date.now();
    const evts = eventsRef.current;

    if (evts.length === 0) {
      setState(prev => ({ ...prev, allComplete: false, nowEvent: null, nextEvent: null }));
      return;
    }

    // Auto-transition check
    for (const event of evts) {
      const startMs = new Date(event.startTime).getTime();
      const endMs = new Date(event.endTime).getTime();

      if (event.status === 'upcoming' && startMs <= now) {
        const key = `${event.id}:active`;
        if (!firedTransitions.current.has(key)) {
          firedTransitions.current.add(key);
          callbackRef.current(event.id, 'active');
        }
      }
      if (event.status === 'active' && endMs <= now) {
        const key = `${event.id}:completed`;
        if (!firedTransitions.current.has(key)) {
          firedTransitions.current.add(key);
          callbackRef.current(event.id, 'completed');
        }
      }
    }

    // All complete check
    const actionable = evts.filter(e => e.status !== 'skipped');
    const allComplete =
      actionable.length > 0 &&
      actionable.every(e => e.status === 'completed' || e.status === 'skipped');

    // Find nowEvent: status is active or upcoming but time range covers now
    const nowEvent = evts.find(e => {
      const startMs = new Date(e.startTime).getTime();
      const endMs = new Date(e.endTime).getTime();
      return (e.status === 'active' || e.status === 'upcoming') && startMs <= now && endMs > now;
    }) ?? null;

    // Find nextEvent: first upcoming event starting after now (and after nowEvent)
    const nowEnd = nowEvent ? new Date(nowEvent.endTime).getTime() : now;
    const nextEvent = evts.find(e => {
      const startMs = new Date(e.startTime).getTime();
      return e.status === 'upcoming' && startMs > nowEnd;
    }) ?? null;

    // Progress percent for nowEvent
    let progressPercent = 0;
    if (nowEvent) {
      const startMs = new Date(nowEvent.startTime).getTime();
      const endMs = new Date(nowEvent.endTime).getTime();
      const duration = endMs - startMs;
      if (duration > 0) {
        progressPercent = Math.min(100, Math.max(0, ((now - startMs) / duration) * 100));
      }
    }

    // Minutes until next
    const minutesUntilNext = nextEvent
      ? Math.max(0, Math.round((new Date(nextEvent.startTime).getTime() - now) / 60000))
      : 0;

    // Countdown target
    let countdownTarget: Date | null = null;
    if (nowEvent) {
      countdownTarget = new Date(nowEvent.endTime);
    } else if (nextEvent) {
      countdownTarget = new Date(nextEvent.startTime);
    }

    // Running over
    const isRunningOver = nowEvent
      ? new Date(nowEvent.endTime).getTime() <= now
      : false;

    setState({
      nowEvent,
      nextEvent,
      progressPercent,
      minutesUntilNext,
      countdownTarget,
      isRunningOver,
      allComplete,
    });
  }, []);

  useEffect(() => {
    compute();
    const id = setInterval(compute, 1000);
    return () => clearInterval(id);
  }, [compute]);

  return state;
}
